import { supabase } from '../supabaseClient';

// Types
export interface PromotionBid {
  id: string;
  user_id: string;
  item_id: string;
  item_type: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE';
  credits_bid: number;
  status: 'ACTIVE' | 'OUTBID' | 'EXPIRED' | 'CANCELLED';
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface TopBid {
  id: string;
  user_id: string;
  credits_bid: number;
  expires_at: string;
}

export interface PromotionBidRequest {
  item_id: string;
  item_type: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE';
  credits_bid: number; // Total credits for the entire duration
  duration_hours?: number; // Duration in hours (converted from days)
}

// Get current user's credits balance
export const getUserCredits = async (): Promise<number> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      console.log('No authenticated user for credits check');
      return 0;
    }

    const { data, error } = await supabase
      .from('user_credits')
      .select('credits_balance')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching user credits:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log('No credits record found for user, returning 0');
      return 0;
    }

    // If multiple records, sum them up and clean up duplicates
    if (data.length > 1) {
      console.warn(`Found ${data.length} credits records for user ${user.id}, summing them up`);
      const totalCredits = data.reduce((sum, record) => sum + (record.credits_balance || 0), 0);
      
      // Clean up duplicates by keeping only the first record with the total
      const { error: deleteError } = await supabase
        .from('user_credits')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('Error cleaning up duplicate credits:', deleteError);
      } else {
        // Create a single record with the total
        const { error: insertError } = await supabase
          .from('user_credits')
          .insert({
            user_id: user.id,
            credits_balance: totalCredits
          });

        if (insertError) {
          console.error('Error creating consolidated credits record:', insertError);
        }
      }

      return totalCredits;
    }

    return data[0]?.credits_balance || 0;
  } catch (error) {
    console.error('Error fetching user credits:', error);
    return 0; // Return 0 instead of throwing to prevent UI crashes
  }
};

// Check if user owns an item
export const checkItemOwnership = async (
  itemId: string, 
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE'
): Promise<boolean> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      console.log('No authenticated user for ownership check');
      return false;
    }

    // For freelancer profiles, check if the item ID matches the user's profile ID
    if (itemType === 'FREELANCER_PROFILE') {
      return itemId === user.id;
    }

    // For services and projects, check ownership in the respective tables
    if (itemType === 'SERVICE') {
      const { data, error } = await supabase
        .from('services')
        .select('provider_id')
        .eq('id', itemId)
        .single();

      if (error || !data) {
        console.log('Service not found or error:', error);
        return false;
      }

      return data.provider_id === user.id;
    }

    if (itemType === 'PROJECT') {
      const { data, error } = await supabase
        .from('projects')
        .select('client_id')
        .eq('id', itemId)
        .single();

      if (error || !data) {
        console.log('Project not found or error:', error);
        return false;
      }

      return data.client_id === user.id;
    }

    return false;
  } catch (error) {
    console.error('Error checking item ownership:', error);
    return false;
  }
};

// Place a promotion bid
export const placePromotionBid = async (bidRequest: PromotionBidRequest): Promise<string> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      throw new Error('User must be authenticated');
    }

    // Check if user has enough credits for the total cost
    const userCredits = await getUserCredits();
    if (bidRequest.credits_bid > userCredits) {
      throw new Error(`Insufficient credits. Required: ${bidRequest.credits_bid}, Available: ${userCredits}`);
    }

    // Check if user owns the item
    const ownership = await checkItemOwnership(bidRequest.item_id, bidRequest.item_type);
    if (!ownership) {
      throw new Error('User does not own this item');
    }

    // Calculate expiration time
    const durationHours = bidRequest.duration_hours || 24;
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + durationHours);

    // Deactivate previous bids for this item by this user
    const { error: deactivateError } = await supabase
      .from('promotion_bids')
      .update({ 
        status: 'CANCELLED',
        updated_at: new Date().toISOString()
      })
      .eq('item_id', bidRequest.item_id)
      .eq('item_type', bidRequest.item_type)
      .eq('user_id', user.id)
      .eq('status', 'ACTIVE');

    if (deactivateError) {
      console.warn('Error deactivating previous bids:', deactivateError);
    }

    // Create new bid
    const { data: bidData, error: insertError } = await supabase
      .from('promotion_bids')
      .insert({
        user_id: user.id,
        item_id: bidRequest.item_id,
        item_type: bidRequest.item_type,
        credits_bid: bidRequest.credits_bid,
        expires_at: expiresAt.toISOString()
      })
      .select()
      .single();

    if (insertError) throw insertError;

    // Deduct credits from user
    const { error: deductError } = await supabase
      .from('user_credits')
      .update({ 
        credits_balance: userCredits - bidRequest.credits_bid,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', user.id);

    if (deductError) {
      console.error('Error deducting credits:', deductError);
      // Try to rollback the bid creation
      await supabase
        .from('promotion_bids')
        .delete()
        .eq('id', bidData.id);
      throw new Error('Failed to deduct credits');
    }

    // Mark other bids as outbid
    const { error: outbidError } = await supabase
      .from('promotion_bids')
      .update({ 
        status: 'OUTBID',
        updated_at: new Date().toISOString()
      })
      .eq('item_id', bidRequest.item_id)
      .eq('item_type', bidRequest.item_type)
      .neq('user_id', user.id)
      .eq('status', 'ACTIVE');

    if (outbidError) {
      console.warn('Error marking other bids as outbid:', outbidError);
    }

    return bidData.id;
  } catch (error) {
    console.error('Error placing promotion bid:', error);
    throw error;
  }
};

// Get top bid for an item
export const getTopPromotionBid = async (
  itemId: string, 
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE'
): Promise<TopBid | null> => {
  try {
    const { data, error } = await supabase.rpc('get_top_promotion_bid', {
      item_id_param: itemId,
      item_type_param: itemType
    });

    if (error) throw error;
    return data?.[0] || null;
  } catch (error) {
    console.error('Error fetching top promotion bid:', error);
    return null;
  }
};

// Get user's bids for an item
export const getUserBidsForItem = async (
  itemId: string, 
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE'
): Promise<PromotionBid[]> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      console.log('No authenticated user for getting bids');
      return [];
    }

    const { data, error } = await supabase
      .from('promotion_bids')
      .select('*')
      .eq('item_id', itemId)
      .eq('item_type', itemType)
      .eq('user_id', user.id) // Only get current user's bids
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user bids:', error);
    return [];
  }
};

// Get all active bids for an item (for display)
export const getActiveBidsForItem = async (
  itemId: string, 
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE'
): Promise<PromotionBid[]> => {
  try {
    // Use the database function instead of direct table access
    const { data, error } = await supabase.rpc('get_top_promotion_bid', {
      item_id_param: itemId,
      item_type_param: itemType
    });

    if (error) throw error;
    
    // Convert the result to PromotionBid format
    if (data && data.length > 0) {
      return [{
        id: data[0].id,
        user_id: data[0].user_id,
        item_id: itemId,
        item_type: itemType,
        credits_bid: data[0].credits_bid,
        status: 'ACTIVE',
        expires_at: data[0].expires_at,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }];
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching active bids:', error);
    return [];
  }
};

// Cancel a bid
export const cancelPromotionBid = async (bidId: string): Promise<void> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      throw new Error('User must be authenticated');
    }

    const { error } = await supabase
      .from('promotion_bids')
      .update({ 
        status: 'CANCELLED',
        updated_at: new Date().toISOString()
      })
      .eq('id', bidId)
      .eq('user_id', user.id); // Only allow users to cancel their own bids

    if (error) throw error;
  } catch (error) {
    console.error('Error cancelling promotion bid:', error);
    throw error;
  }
};

// Get promotion status for an item
export const getPromotionStatus = async (
  itemId: string, 
  itemType: 'SERVICE' | 'PROJECT' | 'FREELANCER_PROFILE'
): Promise<{
  isPromoted: boolean;
  topBid: TopBid | null;
  userBid: PromotionBid | null;
  canBid: boolean;
  userCredits: number;
}> => {
  try {
    // Get ownership first
    const ownership = await checkItemOwnership(itemId, itemType);
    
    // Get credits (this function now handles errors gracefully)
    const userCredits = await getUserCredits();
    
    // Get top bid and user bids only if user owns the item
    let topBid: TopBid | null = null;
    let userBid: PromotionBid | null = null;
    
    if (ownership) {
      try {
        [topBid] = await Promise.all([
          getTopPromotionBid(itemId, itemType)
        ]);
        
        const userBids = await getUserBidsForItem(itemId, itemType);
        userBid = userBids.find(bid => bid.status === 'ACTIVE') || null;
      } catch (bidError) {
        console.warn('Error fetching bid data:', bidError);
        // Continue with null values
      }
    }

    console.log('Promotion status check:', {
      itemId,
      itemType,
      ownership,
      userCredits,
      topBid,
      canBid: ownership && userCredits > 0
    });

    return {
      isPromoted: topBid !== null,
      topBid,
      userBid,
      canBid: ownership && userCredits > 0,
      userCredits
    };
  } catch (error) {
    console.error('Error fetching promotion status:', error);
    return {
      isPromoted: false,
      topBid: null,
      userBid: null,
      canBid: false,
      userCredits: 0
    };
  }
};
